import { PauseCircle } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { cs, de, enUS, pl, vi } from "react-day-picker/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type { BookingPauseDuration } from "@/gql/graphql";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { PauseInput } from "../hooks/use-pause-online-booking";
import { formatPauseEnd } from "../utils/pause-status";

interface PauseBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  onConfirm: (input: PauseInput) => Promise<void>;
  /** Current pause state — when paused the dialog opens in status/resume mode. */
  isPaused: boolean;
  isIndefinite: boolean;
  pausedUntil: string | null;
  pausedMessage: string | null;
  salonTimezone: string;
  onResume: () => Promise<void>;
  /** Open straight on the pause form even while paused (callers whose trigger already says "change pause"). */
  openOnPauseForm?: boolean;
}

const MESSAGE_MAX = 280;

const CALENDAR_LOCALES = new Map([
  ["en", enUS],
  ["vi", vi],
  ["de", de],
  ["pl", pl],
  ["cs", cs],
]);

const DURATION_OPTIONS: {
  value: BookingPauseDuration;
  id: string;
  labelKey: string;
  helperKey?: string;
}[] = [
  {
    value: "FOR_TODAY",
    id: "pause-for-today",
    labelKey: "pauseBookings.forToday",
    helperKey: "pauseBookings.forTodayHelper",
  },
  {
    value: "UNTIL_DATE",
    id: "pause-until-date",
    labelKey: "pauseBookings.untilDate",
  },
  {
    value: "INDEFINITE",
    id: "pause-indefinite",
    labelKey: "pauseBookings.indefinite",
  },
];

interface PauseFormBodyProps {
  duration: BookingPauseDuration;
  onDurationChange: (duration: BookingPauseDuration) => void;
  untilDate: Date | undefined;
  onUntilDateChange: (date: Date | undefined) => void;
  minDate: Date;
  message: string;
  onMessageChange: (message: string) => void;
}

/**
 * Shared form body (duration radios + calendar + message) rendered inside both
 * the desktop Dialog and the mobile Drawer so the pause logic isn't duplicated.
 */
function PauseFormBody({
  duration,
  onDurationChange,
  untilDate,
  onUntilDateChange,
  minDate,
  message,
  onMessageChange,
}: PauseFormBodyProps) {
  const { t, i18n } = useTranslation("settings");

  return (
    <div className="space-y-4">
      <RadioGroup
        value={duration}
        onValueChange={(value) => onDurationChange(value as BookingPauseDuration)}
        className="gap-2"
      >
        {DURATION_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex items-center gap-3 cursor-pointer rounded-lg border p-3 transition-colors",
              duration === option.value
                ? "border-primary bg-primary/5"
                : "border-input hover:bg-muted/50"
            )}
            htmlFor={option.id}
          >
            <RadioGroupItem value={option.value} id={option.id} />
            <span className="flex flex-col">
              <span className="text-sm">{t(option.labelKey)}</span>
              {option.helperKey && (
                <span className="text-xs text-muted-foreground">{t(option.helperKey)}</span>
              )}
            </span>
          </label>
        ))}
      </RadioGroup>

      {duration === "UNTIL_DATE" && (
        <div className="rounded-md border p-2 overflow-x-auto">
          <Calendar
            mode="single"
            selected={untilDate}
            onSelect={onUntilDateChange}
            disabled={{ before: minDate }}
            locale={CALENDAR_LOCALES.get(i18n.language) ?? enUS}
            className="mx-auto"
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="pause-message">{t("pauseBookings.messageLabel")}</Label>
        <Textarea
          id="pause-message"
          placeholder={t("pauseBookings.messagePlaceholder")}
          value={message}
          maxLength={MESSAGE_MAX}
          onChange={(event) => onMessageChange(event.target.value)}
          rows={3}
        />
        <p className="text-xs text-muted-foreground text-right">
          {message.length}/{MESSAGE_MAX}
        </p>
      </div>
    </div>
  );
}

interface PausedStatusBodyProps {
  pauseEnd: string | null;
  pausedMessage: string | null;
}

/** Read-only summary of the active pause, shown instead of the pause form. */
function PausedStatusBody({ pauseEnd, pausedMessage }: PausedStatusBodyProps) {
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
        <PauseCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {pauseEnd
              ? t("pauseBookings.pausedUntil", { date: pauseEnd })
              : t("pauseBookings.pausedIndefinitely")}
          </p>
          <p className="text-xs opacity-80">{t("pauseBookings.pausedNote")}</p>
        </div>
      </div>

      {pausedMessage && (
        <div className="space-y-1.5">
          <Label>{t("pauseBookings.messageShown")}</Label>
          <p className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
            {pausedMessage}
          </p>
        </div>
      )}
    </div>
  );
}

export function PauseBookingDialog({
  open,
  onOpenChange,
  saving,
  onConfirm,
  isPaused,
  isIndefinite,
  pausedUntil,
  pausedMessage,
  salonTimezone,
  onResume,
  openOnPauseForm = false,
}: PauseBookingDialogProps) {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation("settings");
  const [duration, setDuration] = useState<BookingPauseDuration>("FOR_TODAY");
  const [untilDate, setUntilDate] = useState<Date | undefined>(undefined);
  const [message, setMessage] = useState("");
  // While paused the dialog shows the status view; this flips it to the pause
  // form in-place so the owner can change the duration/message.
  const [editingPause, setEditingPause] = useState(openOnPauseForm);

  const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day").toJSDate();
  const dateInvalid = duration === "UNTIL_DATE" && !untilDate;
  const statusMode = isPaused && !editingPause;

  const resetState = () => {
    setDuration("FOR_TODAY");
    setUntilDate(undefined);
    setMessage("");
    setEditingPause(openOnPauseForm);
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) resetState();
  };

  const handleConfirm = async () => {
    if (dateInvalid) return;
    await onConfirm({
      duration,
      untilDate: duration === "UNTIL_DATE" ? untilDate : undefined,
      message,
    });
    handleOpenChange(false);
  };

  const handleResume = async () => {
    await onResume();
    handleOpenChange(false);
  };

  const title = statusMode ? t("pauseBookings.statusTitle") : t("pauseBookings.pauseTitle");
  const description = statusMode
    ? t("pauseBookings.statusDescription")
    : t("pauseBookings.pauseDescription");

  const body = statusMode ? (
    <PausedStatusBody
      pauseEnd={formatPauseEnd(isIndefinite, pausedUntil, salonTimezone, i18n.language)}
      pausedMessage={pausedMessage}
    />
  ) : (
    <PauseFormBody
      duration={duration}
      onDurationChange={setDuration}
      untilDate={untilDate}
      onUntilDateChange={setUntilDate}
      minDate={tomorrow}
      message={message}
      onMessageChange={setMessage}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>

          <div className="overflow-y-auto px-4">{body}</div>

          <DrawerFooter>
            {statusMode ? (
              <>
                <Button onClick={handleResume} disabled={saving} className="w-full">
                  {saving ? t("pauseBookings.resuming") : t("pauseBookings.resume")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditingPause(true)}
                  disabled={saving}
                  className="w-full"
                >
                  {t("pauseBookings.changePause")}
                </Button>
              </>
            ) : (
              <Button onClick={handleConfirm} disabled={saving || dateInvalid} className="w-full">
                {saving ? t("pauseBookings.pausing") : t("pauseBookings.pause")}
              </Button>
            )}
            <DrawerClose asChild>
              <Button variant="ghost" disabled={saving} className="w-full">
                {t("common:actions.cancel")}
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {body}

        <DialogFooter>
          {statusMode ? (
            <>
              <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={saving}>
                {t("common:actions.cancel")}
              </Button>
              <Button variant="outline" onClick={() => setEditingPause(true)} disabled={saving}>
                {t("pauseBookings.changePause")}
              </Button>
              <Button onClick={handleResume} disabled={saving}>
                {saving ? t("pauseBookings.resuming") : t("pauseBookings.resume")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
                {t("common:actions.cancel")}
              </Button>
              <Button onClick={handleConfirm} disabled={saving || dateInvalid}>
                {saving ? t("pauseBookings.pausing") : t("pauseBookings.pause")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
